<template>
  <div class="modern-form manage-patient-wrapper">
    <v-container>
      <v-row class="mt-3">
        <v-col cols="12" sm="12" md="4">
          <div class="patient-sidebar">
            <div class="sidebar-header">
              <h3 class="sidebar-title">Select Patient</h3>
              <div class="search-row">
                <v-text-field
                  label="Search"
                  v-model="search"
                  outlined
                  dense
                  hide-details
                  @keyup.enter="getPatients()"
                  class="search-input"
                  style="color: #414a5b; border-width: 2px" />
                <v-btn
                  class="btn-primary search-btn"
                  :disabled="searchingPatients"
                  @click="getPatients()">Search</v-btn>
              </div>
            </div>
            <div class="sidebar-content">
              <div class="patient-list">
                <div class="text-center">
                  <v-progress-circular
                    v-if="loading"
                    indeterminate
                    color="primary"
                    class="loading-spinner" />
                </div>
                <div v-if="!loading && patients.length === 0" class="no-results">
                  <v-icon class="no-results-icon">mdi-account-off</v-icon>
                  <p class="no-results-text">No patients found</p>
                </div>
                <div v-if="!loading && patients.length > 0" class="patients-list">
                  <div
                    v-for="(item, i) in patients"
                    :key="i"
                    :disabled="searchingPatients"
                    @click="selectPatient(item); selectedPatientIndex = i"
                    class="patient-item"
                    :class="{ 'selected': selectedPatientIndex === i }"
                    id="patientList">
                    <v-avatar class="patient-avatar" size="36">
                      <span class="white--text">{{item.firstName.charAt(0) + item.lastName.charAt(0)}}</span>
                    </v-avatar>
                    <span class="patient-name">{{item.firstName + ' ' + item.lastName}} ({{ item.id }})</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        <div class="pagination-container">
          <div class="pagination-size">
            <v-select
              v-model="pageSize"
              :items="pageSizes"
              label="Items"
              dense
              outlined
              hide-details
              @change="handlePageSizeChange"></v-select>
          </div>
          <v-pagination
            v-model="page"
            :length="totalPages"
            total-visible="5"
            next-icon="mdi-menu-right"
            prev-icon="mdi-menu-left"
            class="custom-pagination">
          </v-pagination>
        </div>
      </v-col>
      <v-col cols="12" sm="12" md="8">
        <div v-if="loading" class="loading-container">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
            class="loading-spinner" />
        </div>
        <div v-else-if="!form.id && Object.keys(form).length === 0" class="no-selection">
          <v-icon class="no-selection-icon">mdi-account-search</v-icon>
          <p class="no-selection-text">No patients were found</p>
          <v-btn class="btn-primary mt-4" @click="addPatient()">
            <v-icon left small>mdi-plus</v-icon>
            Add Patient
          </v-btn>
        </div>
        <div v-else class="info-section">
          <div class="section-header-with-actions">
            <h2 class="section-title">Patient Settings</h2>
            <v-btn class="btn-primary" @click="addPatient()">
              <v-icon left small>mdi-plus</v-icon>
              Add Patient
            </v-btn>
          </div>
          <div class="form-row">
            <v-text-field
              ref="firstNameInput"
              v-model="form.firstName"
              label="First Name"
              class="line-input custom-filled"
              dense
              hide-details="auto" />
            <v-text-field
              v-model="form.lastName"
              label="Last Name"
              class="line-input custom-filled"
              dense
              hide-details="auto" />
          </div>
          <div class="form-row">
            <v-text-field
              type="date"
              v-model="form.birthDate"
              label="Date Of Birth"
              class="line-input custom-filled"
              dense
              hide-details="auto" />
            <v-text-field
              v-model="form.email"
              label="Email"
              class="line-input custom-filled"
              dense
              hide-details="auto" />
          </div>
          <div class="form-row">
            <v-text-field
              v-model="form.address1"
              label="Address Line 1"
              class="line-input custom-filled"
              dense
              hide-details="auto" />
            <v-text-field
              v-model="form.address2"
              label="Address Line 2"
              class="line-input custom-filled"
              dense
              hide-details="auto" />
          </div>
          <div class="form-row">
            <v-text-field
              v-model="form.city"
              label="City"
              class="line-input custom-filled"
              dense
              hide-details="auto" />
            <v-text-field
              v-model="form.state"
              label="State"
              class="line-input custom-filled"
              dense
              hide-details="auto" />
          </div>
          <div class="form-row">
            <v-text-field
              v-model="form.postalCode"
              label="Zip"
              class="line-input custom-filled"
              dense
              hide-details="auto" />
            <v-text-field
              v-model="form.homePhone"
              label="Home Phone"
              class="line-input custom-filled"
              dense
              hide-details="auto" />
          </div>
          <div class="form-row">
            <v-text-field
              v-model="form.workPhone"
              label="Work Phone"
              class="line-input custom-filled"
              dense
              hide-details="auto" />
            <v-text-field
              v-model="form.cellPhone"
              label="Cell Phone"
              class="line-input custom-filled"
              dense
              hide-details="auto" />
          </div>
          <div class="form-row">
            <v-select
              :items="patientStatuses"
              label="Patient Status"
              v-model="form.status"
              class="line-input custom-filled"
              dense
              hide-details="auto" />
          </div>
          <div class="form-actions">
            <v-btn class="btn-primary" @click="savePatient()">Save</v-btn>
            <v-btn class="btn-secondary" @click="cancel()">Cancel</v-btn>
          </div>
        </div>

        <div class="info-section" v-if="!loading && form.id">
          <h2 class="section-title">Actions</h2>
          <div class="action-buttons-container">
            <v-btn @click="showInvitationDialog = true" class="btn-secondary">
              <v-icon left>mdi-email-fast</v-icon>
              Send Portal Invitation
            </v-btn>
            <v-btn @click="showPatientAccount()" class="btn-secondary">
              <v-icon left>mdi-bank</v-icon>
              View Billing
            </v-btn>
            <v-btn @click="showPasswordResetDialog = true" class="btn-secondary">
              <v-icon left>mdi-lock-reset</v-icon>
              Send Password Reset Link
            </v-btn>
            <v-btn @click="toggleShowConfirmChangePasswordDialog()" class="btn-secondary">
              <v-icon left>mdi-lock-open-variant</v-icon>
              Generate New Password
            </v-btn>
            <v-btn @click="showPatientForms()" class="btn-secondary">
              <v-icon left>mdi-form-select</v-icon>
              Assign Forms
            </v-btn>
            <v-btn @click="loadSmsLog()" class="btn-secondary">
              <v-icon left>mdi-comment</v-icon>
              View SMS Log
            </v-btn>
            <v-btn @click="loadPatientConsent()" class="btn-secondary">
              <v-icon left>mdi-account-supervisor</v-icon>
              Manage Consent
            </v-btn>
          </div>
        </div>
      </v-col>

      <v-dialog v-model="invite" max-width="600px" persistant>
        <v-card>
          <v-card-title>Invite New User</v-card-title>
          <v-card-text class="pb-2">
            <v-text-field outlined dense label="Full Name" />
            <v-text-field outlined dense label="Email" />
            <v-btn text color="primary">Send Invite</v-btn>
          </v-card-text>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showInvitationDialog" max-width="500px" persistent>
        <v-card class="modern-modal">
          <v-card-title class="modal-title">Send Portal Invitation</v-card-title>
          <v-card-text class="modal-content">
            <p class="modal-question">Send portal invitation to {{ form.email }}?</p>
            <p style="color: #6b7280; font-size: 14px; margin-top: 12px;">The patient will receive an email with instructions to create their portal account.</p>
          </v-card-text>
          <v-card-actions class="modal-actions">
            <v-btn class="btn-secondary" @click="showInvitationDialog = false">Cancel</v-btn>
            <v-btn class="btn-primary" @click="sendPatientUserInvitation()">Send Invitation</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showPasswordResetDialog" max-width="500px" persistent>
        <v-card class="modern-modal">
          <v-card-title class="modal-title">Send Password Reset</v-card-title>
          <v-card-text class="modal-content">
            <p class="modal-question">Send password reset link to {{ form.email }}?</p>
            <p style="color: #6b7280; font-size: 14px; margin-top: 12px;">The patient will receive an email with instructions to reset their password.</p>
          </v-card-text>
          <v-card-actions class="modal-actions">
            <v-btn class="btn-secondary" @click="showPasswordResetDialog = false">Cancel</v-btn>
            <v-btn class="btn-primary" @click="sendPasswordReset()">Send Reset Link</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showConfirmPasswordDialog" max-width="500px" persistent>
        <v-card class="modern-modal">
          <v-card-title class="modal-title">Generate New Password</v-card-title>
          <v-card-text class="modal-content">
            <p class="modal-question">Generate a new password for {{ form.email }}?</p>
            <p style="color: #6b7280; font-size: 14px; margin-top: 12px;">A new password will be created and emailed to the patient.</p>
          </v-card-text>
          <v-card-actions class="modal-actions">
            <v-btn class="btn-secondary" @click="showConfirmPasswordDialog = false">Cancel</v-btn>
            <v-btn class="btn-primary" @click="changeAndSendPassword()">Generate & Send</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showSmsLogDialog" max-width="700px" persistent>
        <v-card class="modern-modal modal-fixed-actions">
          <v-card-title class="modal-title">SMS Log</v-card-title>
          <v-card-text class="modal-content" id="sms-log">
            <div v-if="loadingSmsLog" class="loading-state">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <div v-if="!loadingSmsLog">
              <div v-if="smsLogs.length === 0" class="no-messages">
                <v-icon class="no-messages-icon">mdi-message-off</v-icon>
                <p class="no-messages-text">No messages found</p>
              </div>
              <v-list v-else v-for="smsLog in smsLogs" :key="smsLog.id">
                <v-list-item :class="smsLog.outgoing ? 'clinic-message' : 'patient-message'">
                  <div class="body">{{ smsLog.message }}</div>
                  <div class="message-date">{{ formatDateAndTime(smsLog.date) }}</div>
                </v-list-item>
              </v-list>
            </div>
          </v-card-text>
          <v-card-actions class="modal-actions">
            <v-btn class="btn-secondary" @click="showSmsDialog = true">New Message</v-btn>
            <v-btn class="btn-primary" @click="showSmsLogDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showSmsDialog" max-width="500px" persistent>
        <v-card class="modern-modal">
          <v-card-title class="modal-title">Send SMS</v-card-title>
          <v-card-text class="modal-content">
            <v-textarea v-model="messageToPatient" outlined dense label="Message" />
          </v-card-text>
          <v-card-actions class="modal-actions">
            <v-btn class="btn-secondary" @click="showSmsDialog = false">Cancel</v-btn>
            <v-btn class="btn-primary" :disabled="sendingSms" @click="sendSms()">Send</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showFormAssignment" max-width="600px" persistent>
        <v-card class="modern-modal modal-fixed-actions">
          <v-card-title class="modal-title">Patient Forms</v-card-title>
          <v-card-text class="modal-content">
            <div v-if="loadingPatientForms" class="loading-state">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <div v-else class="form-assignment-list">
              <div v-for="(form, i) of currentUserForms" :key="i" class="form-assignment-item">
                <div class="form-checkbox-wrapper">
                  <v-checkbox class="form-checkbox" hide-details="auto" v-model="form.assigned" :disabled="!!form.completed">
                    <template v-slot:label>
                      <span class="form-name">{{ form.name }}</span>
                      <span v-if="form.assignAtSignUp" class="form-badge">Assigned at sign up</span>
                    </template>
                  </v-checkbox>
                </div>
                <div v-if="form.assigned" class="form-assignment-details">
                  <div v-for="(assignedForm, j) of form.assignedForms" :key="j" class="assignment-detail">
                    <span class="detail-text">Assigned {{ formatDate(assignedForm.createdAt) }}</span>
                    <span v-if="assignedForm.completedAt" class="detail-completed">
                      Completed {{ formatDate(assignedForm.completedAt) }}
                      <a class="reassign-link" @click="showReassignForm = true; formIdToAssign = form.id">Reassign</a>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="modal-actions">
            <v-btn class="btn-secondary" @click="showFormAssignment = false">Close</v-btn>
            <v-btn class="btn-primary" :disabled="loadingPatientForms || updatingForms" @click="updatePatientFormAssignment()">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showReassignForm" max-width="400px" persistent>
        <v-card class="modern-modal">
          <v-card-title class="modal-title">Assign Form</v-card-title>
          <v-card-text class="modal-content">
            <p class="modal-question">Assign this form again?</p>
          </v-card-text>
          <v-card-actions class="modal-actions">
            <v-btn class="btn-secondary" @click="showReassignForm = false">Cancel</v-btn>
            <v-btn class="btn-primary" :disabled="updatingForms" @click="reassignPatientFormAssignment()">Assign</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showConsentDialog" max-width="600px" persistent>
        <v-card class="modern-modal modal-fixed-actions">
          <v-card-title class="modal-title">Consent Management</v-card-title>
          <v-card-text class="modal-content">
            <div v-if="loadingConsent" class="loading-state">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <div v-if="!loadingConsent" class="consent-sections">
              <div class="consent-section">
                <h4 class="consent-section-title">Patient Can View</h4>
                <div v-if="consentData.viewablePatients?.length" class="consent-list">
                  <div v-for="patient of consentData.viewablePatients" :key="patient.id" class="consent-item">
                    <span class="consent-patient-name">{{ patient.firstName }} {{ patient.lastName }}</span>
                    <v-btn small class="btn-warning" outlined @click="showRevokeConsent(patient, 'to be viewed by')">Revoke</v-btn>
                  </div>
                </div>
                <div v-else class="consent-empty">Patient cannot view any other accounts</div>
              </div>
              
              <div class="consent-section">
                <h4 class="consent-section-title">Patient Is Viewable By</h4>
                <div v-if="consentData.patientsAbleToView?.length" class="consent-list">
                  <div v-for="patient of consentData.patientsAbleToView" :key="patient.id" class="consent-item">
                    <span class="consent-patient-name">{{ patient.firstName }} {{ patient.lastName }}</span>
                    <v-btn small class="btn-warning" outlined @click="showRevokeConsent(patient, 'to view')">Revoke</v-btn>
                  </div>
                </div>
                <div v-else class="consent-empty">Patient cannot be viewed by any other accounts</div>
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="modal-actions">
            <v-btn class="btn-secondary" @click="showProvideAccessDialog = true">Grant Access</v-btn>
            <v-btn class="btn-primary" @click="showConsentDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showProvideAccessDialog" max-width="500px" persistent>
        <v-card class="modern-modal">
          <v-card-title class="modal-title">Grant Access</v-card-title>
          <v-card-text class="modal-content">
            <div v-if="savingConsent" class="loading-state">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <div v-else>
              <p>Choose a patient to grant access. Please ensure and log that this patient has provided written consent</p>
              <div v-if="eligibleFamilyForConsent.length === 0" class="no-eligible-patients">
                No eligible family members found for this patient
              </div>
              <div v-else>
                <v-select v-model="dialogPatient" :items="eligibleFamilyForConsent" clearable>
                  <template v-slot:item="{ item }">{{ item.firstName }} {{ item.lastName }}</template>
                  <template v-slot:selection="{ item }">{{ item.firstName }} {{ item.lastName }}</template>
                </v-select>
                <v-radio-group row v-model="grantAccessType">
                  <v-radio value="viewablePatientId">
                    <template v-slot:label>Can view {{ form.firstName }}</template>
                  </v-radio>
                  <v-radio value="viewingUserId">
                    <template v-slot:label>Can be viewed by {{ form.firstName }}</template>
                  </v-radio>
                </v-radio-group>
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="modal-actions">
            <v-btn class="btn-secondary" @click="showProvideAccessDialog = false">Cancel</v-btn>
            <v-btn class="btn-primary" :disabled="!dialogPatient || !dialogPatient.id" @click="grantAccessForPatient()">Grant Access</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showRevokeAccessDialog" max-width="500px" persistent>
        <v-card class="modern-modal">
          <v-card-title class="modal-title">Confirm Revoking Access</v-card-title>
          <v-card-text class="modal-content">
            <div v-if="savingConsent" class="loading-state">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <p v-else class="modal-question">Are you sure you wish to remove {{ dialogPatient.firstName }} {{ dialogPatient.lastName }}'s ability {{ revokeAbilityText }} {{ form.firstName }} {{ form.lastName }}?</p>
          </v-card-text>
          <v-card-actions class="modal-actions">
            <v-btn class="btn-secondary" @click="showRevokeAccessDialog = false">Cancel</v-btn>
            <v-btn class="btn-warning" @click="revokeAccessForPatient()">Revoke Access</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      </v-row>
    </v-container>
  </div>
</template>
<script>
import DataService from '../../../_services/DataService';
import Utils from '../../utils.js';

export default {
  name: 'PatientManagement',
  props: ['template_props'],
  data() {
    return {
      page: 1,
      totalPages: 0,
      pageSizes: [10, 25, 50, 100, 250],
      pageSize: 10,
      pageName: 'Patient Settings',
      loading: true,
      search: '',
      invite: false,
      isVerifiedUser: false,
      showConfirmPasswordDialog: false,
      showPasswordResetDialog: false,
      showInvitationDialog: false,
      showSmsDialog: false,
      loadingSmsLog: true,
      showSmsLogDialog: false,
      sendingSms: false,
      showFormAssignment: false,
      showReassignForm: false,
      loadingPatientForms: false,
      searchingPatients: false,
      updatingForms: false,
      loadingConsent: false,
      savingConsent: false,
      consentData: {},
      eligibleFamilyForConsent: [],
      dialogPatient: {},
      showConsentDialog: false,
      showProvideAccessDialog: false,
      showRevokeAccessDialog: false,
      revokeAbilityText: '',
      grantAccessType: 'viewablePatientId',
      smsLogs: [],
      messageToPatient: '',
      patients: [],
      selectedUser: 0,
      selectedPatientIndex: null,
      previousSelectedPatientIndex: null,
      patientStatuses: [
        {text: 'Patient', enum: 0, value: 'Patient'},
        {text: 'NonPatient', enum: 1, value: 'NonPatient'},
        {text: 'Inactive', enum: 2, value: 'Inactive'},
        {text: 'Archived', enum: 3, value: 'Archived'},
        {text: 'Deleted', enum: 4, value: 'Deleted'},
        {text: 'Deceased', enum: 5, value: 'Deceased'},
        {text: 'Prospective', enum: 6, value: 'Prospective'},
      ],
      tabs: [
        {label: 'Admin Management', section: ''},
        {label: 'Patient Management', section: ''},
        {label: 'Schedule Management', section: ''},
      ],
      form: {},
      originalForm: {},
      clientForms: [],
      currentUserForms: [],
      formIdToAssign: null,
      formatDate: Utils.formatDate,
      formatDateAndTime: Utils.formatDateAndTime,
    };
  },
  watch: {
    page: {
      async handler() {
        await this.getPatients();
      },
    },
  },
  methods: {
    addPatient() {
      this.previousSelectedPatientIndex = this.selectedPatientIndex;
      this.deselectPatient();
      this.form = { id: null };
      this.$nextTick(() => {
        if (this.$refs.firstNameInput) {
          this.$refs.firstNameInput.focus();
        }
      });
    },
    async handlePageSizeChange(size) {
      this.pageSize = size;
      this.page = 1;
      await this.getPatients();
    },
    async sendPatientUserInvitation() {
      this.showInvitationDialog = false;
      try {
        const body = {
          clientId: DataService.getClientId(),
          patientId: this.form.id,
          patientEmail: this.form.email,
          firstName: this.form.firstName,
          lastName: this.form.lastName,
        };
        const response = await DataService.createInvitation(body);
        if (response.status == 200) {
          this.$root.$emit('raise_alert', {
            color: 'success',
            text: 'Portal invitation sent to ' + this.form.email,
          });
        }
      } catch (err) {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: err.response.data || 'Something went wrong',
        });
      }
    },
    async connectToClient() {
      try {
        const body = {
          clientId: DataService.getClientId(),
          patientId: this.form.id,
          patientEmail: this.form.email,
          userId: this.selectedUser,
        };
        const response = await DataService.connectClientUserToPatient(body);
        if (response.status == 200) {
          this.$root.$emit('raise_alert', {
            color: 'success',
            text: response.data,
          });
        }
      } catch (err) {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: err.response.data || 'Something went wrong',
        });
      }
    },
    toggleShowConfirmChangePasswordDialog() {
      this.showConfirmPasswordDialog = !this.showConfirmPasswordDialog;
    },
    async changeAndSendPassword() {
      this.showConfirmPasswordDialog = false;
      try {
        const response = await DataService.changeAndSendPassword({
          username: this.form.email,
        });
        if (response.status == 200) {
          this.$root.$emit('raise_alert', {
            color: 'success',
            text: 'New password sent to ' + this.form.email,
          });
        }
      } catch (err) {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: err.response.data || 'Something went wrong',
        });
      }
    },
    async sendPasswordReset() {
      this.showPasswordResetDialog = false;
      try {
        const response = await DataService.sendPasswordResetEmail({
          username: this.form.email,
        });
        if (response.status == 200) {
          this.$root.$emit('raise_alert', {
            color: 'success',
            text: 'Reset link sent to ' + this.form.email,
          });
        }
      } catch (err) {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: err.response.data || 'Something went wrong',
        });
      }
    },
    async loadSmsLog() {
      this.showSmsLogDialog = true;
      this.loadingSmsLog = true;

      const response = await DataService.getPatientSmsLog(this.form.id);
      if (response.status == 200) {
        this.smsLogs = response.data;

        setTimeout(() => {
          const logElement = document.getElementById('sms-log');
          logElement.scrollTop = logElement.scrollHeight;
        }, 0);
      } else {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: response.data || 'Something went wrong',
        });
      }

      this.loadingSmsLog = false;
    },
    async sendSms() {
      if (!this.messageToPatient || this.sendingSms) {
        return;
      }

      this.sendingSms = true;

      const response = await DataService.sendPatientSms(this.form.id, this.messageToPatient);
      if (response.status == 200) {
        this.$root.$emit('raise_alert', {
          color: 'success',
          text: 'Message sent',
        });

        this.showSmsDialog = false;
        this.messageToPatient = '';

        await this.loadSmsLog();
      } else {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: response.data || 'Something went wrong',
        });
      }

      this.sendingSms = false;
    },
    async loadPatientConsent() {
      this.showConsentDialog = true;
      this.loadingConsent = true;

      const [consentResponse, eligiblePatientResponse] = await Promise.all([
        DataService.getPatientConsent(this.form.id),
        DataService.getEligiblePatientsForConsent(this.form.id),
      ]);

      if (consentResponse.status === 200 && eligiblePatientResponse.status === 200) {
        this.consentData = consentResponse.data;
        this.eligibleFamilyForConsent = eligiblePatientResponse.data;
      } else {
        this.$root.$emit('raise_alert', { color: 'error', text: 'Something went wrong' });
      }

      this.loadingConsent = false;
    },
    showRevokeConsent(patient, action) {
      this.showRevokeAccessDialog = true;
      this.dialogPatient = patient;
      this.revokeAbilityText = action;
    },
    async revokeAccessForPatient() {
      this.savingConsent = true;
      let viewablePatientId;
      let viewingPatientId;

      if (this.revokeAbilityText === 'to view') {
        viewablePatientId = this.form.id;
        viewingPatientId = this.dialogPatient.id;
      } else {
        viewablePatientId = this.dialogPatient.id;
        viewingPatientId = this.form.id;
      }

      const response = await DataService.revokePatientConsent(viewablePatientId, viewingPatientId);
      if (response.status == 200) {
        this.$root.$emit('raise_alert', { color: 'success', text: 'Access revoked' });
        await this.loadPatientConsent();
        this.showRevokeAccessDialog = false;
      } else {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: response.data || 'Something went wrong',
        });
      }

      this.savingConsent = false;
    },
    async grantAccessForPatient() {
      this.savingConsent = true;
      let viewablePatientId;
      let viewingPatientId;

      if (this.grantAccessType === 'viewablePatientId') {
        viewablePatientId = this.form.id;
        viewingPatientId = this.dialogPatient.id;
      } else {
        viewablePatientId = this.dialogPatient.id;
        viewingPatientId = this.form.id;
      }

      const response = await DataService.grantPatientViewingAccess(viewablePatientId, viewingPatientId);
      if (response.status == 201) {
        this.$root.$emit('raise_alert', { color: 'success', text: 'Access granted' });
        await this.loadPatientConsent();
        this.showProvideAccessDialog = false;
      } else {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: response.data || 'Something went wrong',
        });
      }

      this.savingConsent = false;
    },
    async savePatient() {
      if (!this.form.id) {
        const created = await DataService.createPatient(this.form);
        if (created.status == 201) {
          await this.selectPatient(created.data);
          this.$root.$emit('raise_alert', {
            color: 'success',
            text: 'Patient created successfully!',
          });
        } else {
          this.$root.$emit('raise_alert', {
            color: 'error',
            text: created.data || 'Something went wrong',
          });
        }
      } else {
        const updated = await DataService.updateClientPatientInfo(this.form.id, this.form);
        if (updated.status == 200) {
          this.$root.$emit('raise_alert', {
            color: 'success',
            text: 'Patient updated successfully!',
          });
        } else  {
          this.$root.$emit('raise_alert', {
            color: 'error',
            text: updated.data || 'Something went wrong',
          });
        }
      }
    },
    async getPatientById(patientId) {
      const patientResponse = await DataService.getPatientById(patientId);
      if (patientResponse.status === 200) {
        this.patients = [patientResponse.data];
        this.selectedPatientIndex = 0;
        await this.selectPatient(patientResponse.data);
      }
    },
    cancel() {
      this.form = Object.assign({}, this.originalForm);
      this.selectedPatientIndex = this.previousSelectedPatientIndex;
    },
    async selectPatient(item) {
      this.form = item;
      this.originalForm = Object.assign({}, this.form);
      this.foundUser = false;

      if (item.email) {
        const patientUserResponse = await DataService.getUserByEmail(item.email);
        const foundUser = patientUserResponse.status === 200;
        if (foundUser) {
          this.selectedUser = patientUserResponse.data.id;
        }

        this.isVerifiedUser = foundUser;
      }

      const eighteenYearsAgo = new Date();
      eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);
      this.form.isMinor = !this.form.birthDate || new Date(this.form.birthDate) > eighteenYearsAgo;
    },
    deselectPatient() {
      this.form = {};
      this.selectedPatientIndex = null;
    },
    handleSelectedPatientChange(newPatient) {
      if (newPatient == null && this.previousSelectedPatientIndex != null) {
        // Prevent removing the highlight when a patient is clicked the 2nd time
        this.$nextTick(() => {
          this.selectedPatientIndex = this.previousSelectedPatientIndex;
        });
      } else if (newPatient != null) {
        this.previousSelectedPatientIndex = this.selectedPatientIndex;
      }
    },
    async getPatients() {
      this.loading = true;
      this.searchingPatients = true;

      this.deselectPatient();
      this.originalForm = {};
      this.previousSelectedPatientIndex = null;
      this.selectedPatientIndex = null;

      let params = `?page=${this.page - 1}&size=${this.pageSize}`;
      if (this.search) {
        params = `?page=${this.page - 1}&size=${this.pageSize}&searchTerm=${this.search}`;
      }
      const response = await DataService.getPatients(params);
      this.totalPages = Math.ceil(response.data.recordCount[0].count / this.pageSize) || 0;
      if (response.data.error) {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: response.data.error,
        });
      }

      this.patients = response.data.data.map((pat) => ({ ...pat, birthDate: pat.birthDate.substring(0, 10) }));

      this.loading = false;
      this.searchingPatients = false;

      if (this.patients.length > 0) {
        await this.selectPatient(this.patients[0]);
        this.selectedPatientIndex = 0;
      }
    },
    async getClientForms() {
      const response = await DataService.getClientForms();
      if (response.status === 200) {
        this.clientForms = response.data;
      }
    },
    showPatientAccount() {
      this.$router.push({
        name: 'BillPay',
        query: {
          patientId: this.form.id,
        },
      });
    },
    async showPatientForms() {
      this.showFormAssignment = true;
      this.loadingPatientForms = true;

      const userFormsResponse = await DataService.getFormsForUser(this.selectedUser);
      if (userFormsResponse.status == 200) {
        const userForms = userFormsResponse.data;

        this.currentUserForms = [];
        for (const clientForm of this.clientForms) {
          const assignedForms = userForms.filter((form) => form.formId === clientForm.id);

          this.currentUserForms.push({
            ...clientForm,
            assigned: assignedForms.length > 0,
            completed: assignedForms.some((form) => !!form.completedAt),
            assignedForms,
          });
        }
      } else {
        this.$root.$emit('raise_alert', { text: 'Failed to load patient forms' });
      }

      this.loadingPatientForms = false;
    },
    async updatePatientFormAssignment() {
      this.updatingForms = true;

      const formsToSend = this.currentUserForms.filter((form) => form.assigned).map((form) => form.id);

      const userFormsResponse = await DataService.updateFormsForUser(this.selectedUser, formsToSend);
      if (userFormsResponse.status == 200) {
        this.$root.$emit('raise_alert', { color: 'success', text: 'Form assignments updated' });
      } else {
        this.$root.$emit('raise_alert', { text: 'Failed to update patient forms' });
      }

      this.updatingForms = false;
    },
    async reassignPatientFormAssignment() {
      this.updatingForms = true;

      const assignFormResponse = await DataService.assignFormForUser(this.selectedUser, this.formIdToAssign);
      if (assignFormResponse.status == 201) {
        const formMapping = this.currentUserForms.find((form) => form.id === this.formIdToAssign);
        formMapping.assignedForms.push(assignFormResponse.data);

        this.formIdToAssign = null;
        this.showReassignForm = false;

        this.$root.$emit('raise_alert', { color: 'success', text: 'Form assigned' });
      } else {
        this.$root.$emit('raise_alert', { text: 'Failed to update patient forms' });
      }

      this.updatingForms = false;
    },
  },
  async mounted() {
    this.loading = true;

    await this.getClientForms();

    if (this.$route.query.patientId) {
      await this.getPatientById(this.$route.query.patientId);
    } else {
      await this.getPatients();
    }

    this.loading = false;
  },
};
</script>

<style lang="scss" scoped>
@import '@/scss/variables.scss';

$patient-card-border-color: #e2e8f0;
$section-title-color: #1f2937;
$action-button-gap: 12px;

.patient-sidebar {
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: $shadow-md;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  height: fit-content;
  
  .sidebar-header {
    padding: 24px 24px 16px 24px;
    border-bottom: 1px solid #e2e8f0;
    
    .sidebar-title {
      font-size: 18px;
      font-weight: 600;
      color: #2d3748;
      margin: 0 0 16px 0;
      font-family: 'Inter', sans-serif;
    }
    
    .search-row {
      display: flex;
      gap: 8px;
      align-items: flex-start;
      
      .search-input {
        flex: 1;
        min-width: 0;
      }
      
      .search-btn {
        flex-shrink: 0;
        min-width: 80px;
      }
    }
  }
  
  .sidebar-content {
    padding: 16px;
  }
}

.patient-list {
  .no-results {
    text-align: center;
    padding: 40px 16px;
    
    .no-results-icon {
      font-size: 48px;
      color: #cbd5e0;
      margin-bottom: 12px;
    }
    
    .no-results-text {
      color: #718096;
      margin: 0;
      font-size: 14px;
    }
  }
  
  .patients-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .patient-item {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    background-color: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      background-color: #edf2f7;
      border-color: #cbd5e0;
    }
    
    &.selected {
      background-color: #f0f9e8 !important;
      border-color: #B0D35E !important;
      
      .patient-name {
        color: #414a5b !important;
        font-weight: 600 !important;
      }
    }
    
    .patient-avatar {
      background-color: #133b54 !important;
      color: white !important;
      margin-right: 12px !important;
    }
    
    .patient-name {
      font-weight: 500 !important;
      color: #1f2937 !important;
      font-size: 14px !important;
    }
  }
}

.pagination-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 16px;
  padding: 12px 0;
  padding-left: 16px;
  
  .pagination-size {
    width: 90px;
  }
  
  .custom-pagination {
    ::v-deep .v-pagination {
      &__item {
        box-shadow: none;
        border: 1px solid #e2e8f0;
        
        &--active {
          background-color: #133b54 !important;
          color: white !important;
          border-color: #133b54 !important;
        }
      }

      &__more {
        width: 22px;
        margin-left: 0;
        margin-right: 0;
      }
      
      &__navigation {
        box-shadow: none;
        border: 1px solid #e2e8f0;
      }
    }
  }
}

.no-selection {
  text-align: center;
  padding: 80px 24px;
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: $shadow-md;
  border: 1px solid #e2e8f0;
  
  .no-selection-icon {
    font-size: 64px;
    color: #cbd5e0;
    margin-bottom: 16px;
  }
  
  .no-selection-text {
    color: #718096;
    margin: 0;
    font-size: 16px;
  }
}

.loading-container {
  text-align: center;
  padding: 80px 24px;
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: $shadow-md;
  border: 1px solid #e2e8f0;
}

.section-header-with-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 16px;

  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: $section-title-color;
    margin: 0;
    border: none;

  }
}

.action-buttons-container {
  display: flex;
  flex-wrap: wrap;
  gap: $action-button-gap;
  margin-top: 16px;
}

.v-card {
  border: 1px solid $patient-card-border-color !important;
  box-shadow: $shadow-sm !important;
}

.loading-spinner {
  display: block;
  margin: 40px auto;
}

.pageNum {
  color: white;
}

.theme--light.v-sheet--outlined {
  border: thin solid #414a5b;
}
.buttons {
  color: #414a5b;
  /* background-color: #b0d35e; */
  font-family: 'Inter', sans-serif;
  font-size: 12px !important;
  font-weight: 700;
  text-transform: capitalize;
  letter-spacing: normal;
  padding: 20px;
}
.deleteBtn {
  color: white;
}
.selectedItemClass button {
  background-color: #414a5b;
  color: white;
}

.v-list-item__title {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 700;
  text-align: left;
  text-transform: capitalize;
}
.v-list-item--disabled {
  opacity: 0.65;
}

#sms-log {
  .v-list {
    padding: 0;
    margin-bottom: 16px;
  }
  
  .v-list-item {
    padding: 0;
    min-height: auto;
  }
  
  .no-messages {
    text-align: center;
    padding: 40px 16px;
    
    .no-messages-icon {
      font-size: 48px;
      color: #cbd5e0;
      margin-bottom: 12px;
    }
    
    .no-messages-text {
      color: #718096;
      margin: 0;
      font-size: 14px;
    }
  }
}

.clinic-message, .patient-message {
  max-width: 70%;
  margin-bottom: 12px;
  
  .body {
    border-radius: 12px;
    padding: 10px 14px;
    word-wrap: break-word;
    line-height: 1.4;
  }
  
  .message-date {
    font-size: 11px;
    color: #9ca3af;
    margin-top: 4px;
    padding: 0 4px;
  }
}

.clinic-message {
  margin-left: auto;
  text-align: right;
  
  .body {
    background-color: #133b54;
    color: white;
  }
}

.patient-message {
  margin-right: auto;
  text-align: left;
  
  .body {
    background-color: #f3f4f6;
    color: #1f2937;
  }
}

.consent-sections {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.consent-section {
  .consent-section-title {
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 12px;
  }
  
  .consent-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .consent-item {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    gap: 12px;
    
    .consent-patient-name {
      font-size: 14px;
      color: #1f2937;
      font-weight: 500;
    }
  }
  
  .consent-empty {
    padding: 16px;
    text-align: center;
    color: #9ca3af;
    font-size: 14px;
    font-style: italic;
  }
}

.form-assignment-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-assignment-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  background-color: #f9fafb;
  
  .form-checkbox-wrapper {
    .form-checkbox {
      margin: 0;
      
      ::v-deep .v-label {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
      }
    }
    
    .form-name {
      font-size: 14px;
      font-weight: 500;
      color: #1f2937;
    }
    
    .form-badge {
      font-size: 12px;
      color: #6b7280;
      background-color: #e5e7eb;
      padding: 2px 8px;
      border-radius: 4px;
    }
  }
  
  .form-assignment-details {
    margin-top: 12px;
    margin-left: 32px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    
    .assignment-detail {
      font-size: 13px;
      
      .detail-text {
        color: #6b7280;
      }
      
      .detail-completed {
        color: #059669;
        margin-left: 8px;
        
        .reassign-link {
          color: #2563eb;
          text-decoration: underline;
          cursor: pointer;
          margin-left: 8px;
          
          &:hover {
            color: #1d4ed8;
          }
        }
      }
    }
  }
}

.no-eligible-patients {
  padding: 16px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
  margin-top: 12px;
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
}

// Wrapper styling to prevent pagination overflow
// When there are many pages, the pagination can extend beyond the container width
.manage-patient-wrapper {
  .v-container {
    padding-bottom: 32px;
    max-width: 100%;
    overflow-x: hidden;
  }

  .pagination-container {
    max-width: 100%;
    overflow-x: auto;
  }
}
</style>

